import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { getQuarter } from 'date-fns';
import { map } from 'rxjs/operators';

import { CreateAssessmentDialogStoreActions } from '@ninety/mobile/pages-shared/assessments-shared';
import { HeadlinesActions } from '@ninety/mobile/pages-shared/headlines-shared';
import { IssuesActions } from '@ninety/mobile/pages-shared/issues-shared';
import { RockActions, RockModel, RockQuarter } from '@ninety/mobile/pages-shared/shared-rocks';
import { TeamTodosStateActions } from '@ninety/mobile/pages-shared/to-dos-shared';
import { LinkedItemActions } from '@ninety/mobile/platform/components';
import { SegmentTrackEvent } from '@ninety/mobile/platform/services';
import { SegmentActions } from '@ninety/mobile/platform/state';

@Injectable()
export class UniversalCreateAnalyticsEffects {
  private readonly actions$ = inject(Actions);

  trackTodoCreated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TeamTodosStateActions.universalCreateSuccess),
      map(({ todo }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.TODO_CREATED,
          params: {
            entityId: todo._id,
            isPersonal: todo.isPersonal ?? false,
            teamId: todo.teamId,
            userId: todo.userId,
            eventSource: 'regular create',
            repeat: todo.repeat,
          },
        })
      )
    )
  );

  trackIssueCreated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.addCreatedIssue),
      map(({ issue }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.ISSUE_CREATED,
          params: {
            entityId: issue._id,
            eventSource: 'regular create',
            intervalCode: issue.intervalCode,
            isPublic: issue.isPublic,
            rating: issue.rating,
            teamId: issue.teamId,
            userId: issue.userId,
          },
        })
      )
    )
  );

  trackRockCreated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RockActions.universalCreateSuccess),
      map(({ rock }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.ROCK_CREATED,
          params: {
            ...this.mapRockToSegmentSchema(rock),
            eventSource: 'regular create',
          },
        })
      )
    )
  );

  trackAssessmentCreated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CreateAssessmentDialogStoreActions.createCompanyAssessmentSuccess),
      map(({ companyAssessment }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.ASSESSMENT_CREATED,
          params: {
            entityId: companyAssessment.companyAssessmentId,
            eventSource: 'regular create',
            teamIds: (companyAssessment.teamIds ?? []).join(','),
          },
        })
      )
    )
  );

  trackHeadlineCreated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesActions.universalCreateSuccess),
      map(({ headline }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.HEADLINE_CREATED,
          params: {
            entityId: headline._id,
            eventSource: 'regular create',
            teamId: headline.teamId,
            userId: headline.ownedByUserId,
          },
        })
      )
    )
  );

  /**
   * Track when a linked item (to-do / issue / headline) is created from a parent item's detail screen.
   * `entityType` (created item) and `parentEntity` (linked-to item) are polymorphic, so they ride on the
   * action rather than the registry. The wire field for the parent id is `parentEntityID`.
   */
  trackLinkedItemCreated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LinkedItemActions.linkedItemCreated),
      map(({ entityId, entityType, parentEntity, parentEntityId }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.LINKED_ITEM_CREATED,
          params: {
            entityId,
            entityType,
            parentEntity,
            parentEntityID: parentEntityId,
          },
        })
      )
    )
  );

  /**
   * Schema-aligned Segment `track` payload for a rock, matching the Master Segment Track Events sheet.
   * Emits `entityId`/rock-context fields; `entityType` comes from the registry, not here.
   */
  private mapRockToSegmentSchema(rock: RockModel) {
    const RockQuarterList = [RockQuarter.none, RockQuarter.Q1, RockQuarter.Q2, RockQuarter.Q3, RockQuarter.Q4] as const;

    return {
      entityId: rock._id,
      additionalTeamIds: (rock?.additionalTeamIds ?? []).join(','),
      dueDate: rock.dueDate,
      levelCode: rock.levelCode,
      statusCode: rock.statusCode,
      teamId: rock.teamId,
      userId: rock.userId,
      quarter: rock.quarter ?? (rock.dueDate ? RockQuarterList[getQuarter(rock.dueDate)] : undefined),
    };
  }
}
